import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Box, useMediaQuery, useTheme, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";

import { QuizePopUp } from "../quiz-pop-up/quiz-pop-up";
import { AssessmentPopUp } from "../quiz-pop-up/assessment-pop-up";
import { AssessmentListModal } from "../assessment-list-modal/assessment-list-modal";
import { AnimatePresenceFixedType } from "../../helpers/animatePresenceFixedType";
import { ChevronDownIcon, MdLeaderboard } from "../../assets/icon";
import { MessageBubble } from "./chat-bubble/message-bubble";
import { MessageFooter } from "./message-footer/message-footer";
import { ChatHeader } from "./chat-header";
import { useSocket, usePhotoUploader, useFileUploader } from "../../utils";
import { useUserStore } from "../../stores/userStore";
import { useChatStore } from "../../stores/chatStore";
import { useQuizeStore } from "../../stores/quizeStore";
import { useAssessmentStore } from "../../stores/assessmentStore";
import { ProgressBarsBlocks } from "../progress-bars-blocks/progress-bars-blocks";
import { LeaderBoard } from "../leader-board/leader-board";
import { PioneerLounge } from "../pioneer-lounge/pioneer-lounge";
import { MmkSection } from "../mmk-section/mmk-section";
import { LessonOnboarding } from "../lesson-onboarding/lesson-onboarding";
import { MinimalAudioPlayer } from "./chat-bubble/minimal-audio-player/minimal-audio-player";
import { useAppStore } from "../../stores/appStore";
import { useStatisticsStore } from "../../stores/statisticsStore";
import {
  scrollToBottomJust,
  scrollToBottomInitial,
  scrollToBottomSmooth,
  scrollToNextUserMessage,
  scrollToPreviousUserMessage,
  pinScrollToBottom,
} from "../../utils/scrollUtils";
import { filterLessonFeedMessages } from "../../utils/messageCache";
import {
  isBrowserOffline,
  notifyOfflineActionBlocked,
} from "../../utils/internetConnection";
import { handleFileChange } from "../../utils/handleFileChange";
import {
  useInitialMessages,
  useMessageSending,
  useScrollHandling,
  useScrollToMessage,
  useTargetMessageEffect,
  useLessonsToDo,
} from "../../hooks";
import { useBrowserDetection } from "../../hooks/useBrowserDetection";
import { useUpdateEffect } from "react-use";
import { FeedbackSupportButton } from "../feedback-support/feedback-support-button";
import { getMainMenu } from "../../axios/buttons";

const MotionBox = motion(Box);
const MIN_SCROLL_TOP = 20;

const TripleChevron: React.FC<{
  size?: number;
  color?: string;
  isExpanded?: boolean;
  animKey?: number;
}> = ({ size = 24, color, isExpanded }) => {
  const layer = (
    toTop: number,
    fromTop: number | undefined,
    className: string,
    zIndex: number,
  ) => (
    <ChevronDownIcon
      size={size}
      style={{
        position: "absolute",
        left: 0,
        color,
        zIndex,
        transform: "rotate(180deg)",
        ...(fromTop !== undefined
          ? ({
              ["--fromTop" as any]: `${fromTop}px`,
            } as React.CSSProperties)
          : {}),
        ["--toTop" as any]: `${toTop}px`,
      }}
      className={className}
    />
  );

  // Layout: make room for 3 stacked chevrons without clipping.
  const gap = 5;
  const topTop = 0;
  const middleTop = gap;
  const bottomTop = 2 * gap;
  const spawnFromTop = bottomTop + 12;

  return (
    <span
      style={{
        position: "relative",
        display: "inline-block",
        width: size,
        height: size + bottomTop,
        overflow: "visible",
      }}
    >
      {layer(bottomTop, undefined, "triple-chevron-layer bottom", 1)}
      {layer(middleTop, spawnFromTop, "triple-chevron-layer middle", 2)}
      {layer(topTop, spawnFromTop, "triple-chevron-layer top", 3)}
    </span>
  );
};

const Chat = () => {
  const { clearStore, fetchPreassessment, preassessmentData } = useQuizeStore();
  const { clearStore: clearAssessmentStore } = useAssessmentStore();

  const theme = useTheme();
  const { user, token, logout } = useUserStore();
  const {
    focus,
    domain,
    setDomainFocus,
    restoreOriginalDomainFocus,
    isBrowserLocked,
    setRefreshChat,
    refreshChat,
    showLessonOnboarding,
    messageTheme,
    updateMessageTheme,
    feedBackAnimationFinish,
    setFeedBackAnimationFinish,
    isPreassessmentOpen,
    setIsPreassessmentOpen,
    isAssessmentOpen,
    setIsAssessmentOpen,
    setDeviceConflictModalOpen,
    startRecordingTrigger,
    triggerAvatarRingsAnimation,
    sellingDialogue,
    isUserMenuModalOpen,
    isOnline,
  } = useAppStore();

  const closeQuizModal = async () => {
    await clearStore();
    setIsPreassessmentOpen(false);
  };

  const closeAssessmentModal = async () => {
    try {
      const { checkSessionStatus } = await import("../../axios/user");
      const sessionStatus = await checkSessionStatus();
      if (sessionStatus.status === "conflict") {
        setDeviceConflictModalOpen(true);
      }
    } catch (error) {
      console.error("Error checking session status on close:", error);
      // Continue with close if check fails
    }

    clearAssessmentStore();
    setIsAssessmentOpen(false);
  };

  useUpdateEffect(() => {
    // Don't fetch preassessment if roleplay parameter is present
    const urlParams = new URLSearchParams(window.location.search);
    const hasRoleplay = urlParams.get("roleplay");
    const savedParams = sessionStorage.getItem("pendingChallengeData");
    let hasRoleplayInStorage = false;

    if (savedParams) {
      try {
        const params = JSON.parse(savedParams);
        hasRoleplayInStorage = !!params.roleplay;
      } catch (error) {
        // Ignore parsing errors
      }
    }

    if (hasRoleplay || hasRoleplayInStorage) {
      return;
    }

    const fetchData = async () => {
      try {
        setIsPreassessmentOpen(false);
        await clearStore();
        await fetchPreassessment();
      } catch (error) {
        console.error("Error fetching quiz data:", error);
      }
    };
    fetchData();
  }, [domain, focus, fetchPreassessment, clearStore, setIsPreassessmentOpen]);

  useEffect(() => {
    if (isPreassessmentOpen || isAssessmentOpen) {
      setIsHeaderVisible(false);
      setManualOverride(true);
    }
  }, [isPreassessmentOpen, isAssessmentOpen]);

  useEffect(() => {
    // Don't open preassessment if roleplay parameter is present
    const urlParams = new URLSearchParams(window.location.search);
    const hasRoleplay = urlParams.get("roleplay");
    const savedParams = sessionStorage.getItem("pendingChallengeData");
    let hasRoleplayInStorage = false;

    if (savedParams) {
      try {
        const params = JSON.parse(savedParams);
        hasRoleplayInStorage = !!params.roleplay;
      } catch (error) {
        // Ignore parsing errors
      }
    }

    if (hasRoleplay || hasRoleplayInStorage) {
      if (isPreassessmentOpen) {
        setIsPreassessmentOpen(false);
      }
      return;
    }

    if (preassessmentData.questions && preassessmentData.questions.length > 0) {
      setIsPreassessmentOpen(true);
    } else {
      setIsPreassessmentOpen(false);
    }
  }, [preassessmentData, isPreassessmentOpen, setIsPreassessmentOpen]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getUrlParam = (key: string): string | null => {
    const fromSearchParams = searchParams.get(key);
    if (fromSearchParams) return fromSearchParams;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(key);
    } catch {
      return null;
    }
  };

  const urlTargetMessageId = getUrlParam("message_id");
  const urlDomain = getUrlParam("domain");
  const urlFocus = getUrlParam("focus");
  const urlRoleplay = getUrlParam("roleplay");
  const urlMode = getUrlParam("mode");

  const { setRefreshedLeaderBoard, setRefreshedProgressBar } =
    useStatisticsStore();

  const {
    messages,
    fetchInitialMessages,
    fetchInitialMessagesWithoutClearing,
    loadOlderMessages,
    loadNewerMessages,
    setMessages,
    setTargetMessageId,
    targetMessageId,
    sendMessage,
    uploadFile,
    fetchUserMessagePagination,
    fetchLessonPagination,
    cleanMessageArray,
    bootstrapChat,
    bootstrapGeneration,
    isBootstrapping,
    isOpenMiniPlayer,
    handleToggleMiniPlayer,
    messageAudio,
    refetchTargetMessage,
    copyMessage,
    setCopyMessage,
    waitForAnimation,
    setWaitForAnimation,
    canPaginateDown,
    canPaginateUp,
    fetchLessonsToDo,
    refreshScheduled,
    setRefreshScheduled,
    scheduledTargetMessageId,
    setScheduledTargetMessageId,
  } = useChatStore();

  const { sendMessage: sendMessageSocket } = useSocket(
    setMessages,
    token || "",
    () => {
      setStreamingTrigger(true);
    },
    setRefreshScheduled,
    setScheduledTargetMessageId,
  );

  const [streamingTrigger, setStreamingTrigger] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [manualOverride, setManualOverride] = useState(true);
  const [chevronAnimKey, setChevronAnimKey] = useState(0);
  const [lastUserMessageHeight, setLastUserMessageHeight] = useState(264);
  const lastScrollTopRef = useRef(0);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSearchingMessage, setIsSearchingMessage] = useState(false);
  const animationTimeoutRef = useRef<number | null>(null);
  const skipRefetchRef = useRef(false);
  const handledBootstrapGenerationRef = useRef(0);
  const bootstrapPinCancelRef = useRef<(() => void) | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const loadingOlder = false;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight && isSmallScreen);
    };

    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, [isSmallScreen]);

  const scrollThresholdUp = isSmallScreen ? 0.00001 : 0.3;
  const scrollThresholdDown = isSmallScreen ? 0.95 : 0.95;
  const previousTargetMessageId = useRef(targetMessageId);
  const isProcessingUrlTarget = useRef(false);
  const initialLoadCompleted = useRef(false);
  const previousMessageTheme = useRef(messageTheme);
  const messageNotFoundRef = useRef(false);
  const themeChangeFromMessageRef = useRef(false);
  const processedModeDeepLinkRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messageInViewIdRef = useRef<string | null>(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [feedbackCommand, setFeedbackCommand] = useState("/feedback");
  const { isUploading, handlePhotoUpload } = usePhotoUploader(
    (action) => setMessages(action),
    user,
  );
  const { isUploadingFile, handleFileUpload } = useFileUploader(
    (action) => setMessages(action),
    user,
  );

  const openVoiceRecorder = () => {
    setShowVoiceRecorder(true);
  };

  const closeVoiceRecorder = () => {
    setShowVoiceRecorder(false);
  };

  useUpdateEffect(() => {
    if (!startRecordingTrigger) return;
    setShowVoiceRecorder(true);
  }, [startRecordingTrigger]);

  useEffect(() => {
    let isMounted = true;

    getMainMenu()
      .then((data: Array<{ text?: string; command?: string }>) => {
        if (!isMounted) return;
        const feedbackItem = data.find((item) =>
          (item.text || "").toLowerCase().includes("feedback"),
        );
        if (feedbackItem?.command) {
          setFeedbackCommand(feedbackItem.command);
        }
      })
      .catch(() => {
        // Keep fallback /feedback command if menu is not available.
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const uniqueMessages = useMemo(() => {
    const deduped = messages.filter(
      (msg, index, self) =>
        index === self.findIndex((m) => m.message_id === msg.message_id),
    );

    if (messageTheme === "lessons" && !isOnline) {
      return filterLessonFeedMessages(deduped);
    }

    return deduped;
  }, [messages, messageTheme, isOnline]);

  const showToast = (
    message: string,
    type: "success" | "error" | "info" = "info",
  ) => {
    toast.dismiss();

    const options = {
      position: "bottom-center" as const,
      autoClose: 3000,
      closeOnClick: false,
      pauseOnHover: true,
    };

    switch (type) {
      case "success":
        toast.success(message, options);
        break;
      case "error":
        toast.error(message, options);
        break;
      default:
        toast.info(message, options);
    }
  };

  const cleanupUrl = () => {
    try {
      const url = new URL(window.location.href);
      const path = url.pathname;
      window.history.replaceState({}, "", path);
    } catch (error) {}
  };

  useEffect(() => {
    let currentMessageId = urlTargetMessageId;
    let currentDomain = urlDomain;
    let currentFocus = urlFocus;

    if (!currentDomain || !currentFocus) {
      const savedParams = sessionStorage.getItem("pendingChallengeData");
      if (savedParams) {
        try {
          const params = JSON.parse(savedParams);
          if (!currentDomain && params.domain) currentDomain = params.domain;
          if (!currentFocus && params.focus) currentFocus = params.focus;
          if (!currentMessageId && params.message_id)
            currentMessageId = params.message_id;
        } catch (error) {
          console.error("Error parsing sessionStorage:", error);
        }
      }
    }

    if (currentMessageId && currentDomain && currentFocus) {
      if (
        (currentDomain && currentDomain !== domain) ||
        (currentFocus && currentFocus !== focus)
      ) {
        setDomainFocus(currentDomain || domain, currentFocus || focus, true);
      }
    }
  }, [
    token,
    domain,
    focus,
    setDomainFocus,
    urlDomain,
    urlFocus,
    urlTargetMessageId,
  ]);

  useEffect(() => {
    if (!token) return;
    if (processedModeDeepLinkRef.current) return;

    let mode = urlMode;
    if (!mode) {
      const savedParams = sessionStorage.getItem("pendingChallengeData");
      if (savedParams) {
        try {
          const params = JSON.parse(savedParams) as { mode?: string };
          mode = params.mode ?? null;
        } catch (error) {
          console.error("Error parsing sessionStorage:", error);
        }
      }
    }

    if (!mode) return;

    const normalizedMode = mode.trim().toLowerCase();
    const shouldOpenMmm = normalizedMode === "mmm" || normalizedMode === "mmk";
    if (!shouldOpenMmm) return;

    processedModeDeepLinkRef.current = true;
    updateMessageTheme("mmk");

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      const query = url.searchParams.toString();
      window.history.replaceState({}, "", query ? `?${query}` : url.pathname);
    } catch (error) {
      console.error("Error cleaning mode query param:", error);
    }

    const savedParams = sessionStorage.getItem("pendingChallengeData");
    if (savedParams) {
      try {
        const params = JSON.parse(savedParams) as Record<string, unknown>;
        if ("mode" in params) {
          delete params.mode;
          sessionStorage.setItem(
            "pendingChallengeData",
            JSON.stringify(params),
          );
        }
      } catch (error) {
        console.error("Error updating pendingChallengeData mode:", error);
      }
    }
  }, [token, urlMode, updateMessageTheme]);

  const isProcessingRoleplayData = useRef(false);

  useEffect(() => {
    if (!token) {
      return;
    }

    const handleRoleplayData = async () => {
      const currentUrlParams = new URLSearchParams(window.location.search);
      let currentUrlRoleplay = currentUrlParams.get("roleplay");
      let currentUrlDomain = currentUrlParams.get("domain");
      let currentUrlFocus = currentUrlParams.get("focus");
      let currentUrlMode = currentUrlParams.get("mode");

      if (!currentUrlRoleplay || !currentUrlDomain || !currentUrlFocus) {
        const savedParams = sessionStorage.getItem("pendingChallengeData");
        if (savedParams) {
          try {
            const params = JSON.parse(savedParams);

            if (!currentUrlRoleplay && params.roleplay) {
              currentUrlRoleplay = params.roleplay;
            }
            if (!currentUrlDomain && params.domain) {
              currentUrlDomain = params.domain;
            }
            if (!currentUrlFocus && params.focus) {
              currentUrlFocus = params.focus;
            }
            if (!currentUrlMode && params.mode) {
              currentUrlMode = params.mode;
            }

            const searchParams = new URLSearchParams();
            if (currentUrlRoleplay)
              searchParams.set("roleplay", currentUrlRoleplay);
            if (currentUrlDomain) searchParams.set("domain", currentUrlDomain);
            if (currentUrlFocus) searchParams.set("focus", currentUrlFocus);
            if (currentUrlMode) searchParams.set("mode", currentUrlMode);

            const queryString = searchParams.toString();
            if (queryString) {
              window.history.replaceState({}, "", `?${queryString}`);
            }
          } catch (error) {
            console.error("Error parsing sessionStorage:", error);
          }
        }
      }

      if (
        currentUrlRoleplay &&
        currentUrlDomain &&
        currentUrlFocus &&
        !isProcessingRoleplayData.current
      ) {
        isProcessingRoleplayData.current = true;

        try {
          if (isPreassessmentOpen) {
            setIsPreassessmentOpen(false);
          }
          if (isAssessmentOpen) {
            setIsAssessmentOpen(false);
          }

          if (currentUrlDomain !== domain || currentUrlFocus !== focus) {
            await setDomainFocus(currentUrlDomain, currentUrlFocus, true);
            await new Promise((resolve) => setTimeout(resolve, 500));
          }

          await new Promise((resolve) => setTimeout(resolve, 800));

          const openRealtimeMenuEvent = new CustomEvent("openRealtimeMenu");
          window.dispatchEvent(openRealtimeMenuEvent);

          setTimeout(() => {
            cleanupUrl();
          }, 500);
        } catch (error) {
          console.error("Error processing roleplay data:", error);
        } finally {
          isProcessingRoleplayData.current = false;
        }
      }
    };

    const timeout1 = setTimeout(() => {
      handleRoleplayData();
    }, 500);

    const timeout2 = setTimeout(() => {
      if (!isProcessingRoleplayData.current) {
        handleRoleplayData();
      }
    }, 2000);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
  }, [
    token,
    urlRoleplay,
    urlDomain,
    urlFocus,
    domain,
    focus,
    setDomainFocus,
    isPreassessmentOpen,
    isAssessmentOpen,
    setIsPreassessmentOpen,
    setIsAssessmentOpen,
  ]);

  const handleAnimationStart = () => {
    setIsAnimating(true);
    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    animationTimeoutRef.current = window.setTimeout(() => {
      setIsAnimating(false);
    }, 1000);
  };

  useInitialMessages({
    messageTheme,
    targetMessageId,
    messagesContainerRef,
    previousMessageThemeRef: previousMessageTheme,
    initialLoadCompletedRef: initialLoadCompleted,
    setIsSearchingMessage,
    setTargetMessageId,
    cleanMessageArray,
    scrollToBottomInitial,
    themeChangeFromMessageRef,
  });

  useLayoutEffect(() => {
    handleAnimationStart();
  }, [showVoiceRecorder]);

  useEffect(() => {
    let messageId = urlTargetMessageId;

    if (!messageId) {
      const savedParams = sessionStorage.getItem("pendingChallengeData");
      if (savedParams) {
        try {
          const params = JSON.parse(savedParams);
          if (params.message_id) {
            messageId = params.message_id;

            const currentUrlParams = new URLSearchParams(
              window.location.search,
            );
            if (!currentUrlParams.get("message_id")) {
              currentUrlParams.set("message_id", params.message_id);
              window.history.replaceState(
                {},
                "",
                `?${currentUrlParams.toString()}`,
              );
            }
          }
        } catch (error) {
          console.error("Error parsing sessionStorage for message_id:", error);
        }
      }
    }

    if (messageId && !isProcessingUrlTarget.current) {
      isProcessingUrlTarget.current = true;
      setTargetMessageId(messageId);
    }
  }, [urlTargetMessageId, token, setTargetMessageId]);

  useEffect(() => {
    if (!feedBackAnimationFinish || messages.length < 2) return;

    const prevMsg = messages[messages.length - 2];

    if (
      (prevMsg.message_type === "feedback" ||
        prevMsg.message_type === "suggestion") &&
      feedBackAnimationFinish
    ) {
      setFeedBackAnimationFinish(false);
    }
  }, [feedBackAnimationFinish, messages, setFeedBackAnimationFinish]);

  useEffect(() => {
    if (streamingTrigger) {
      if (isAtBottom && !targetMessageId && messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }

      const timeout = setTimeout(() => {
        setStreamingTrigger(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [streamingTrigger, isAtBottom, targetMessageId]);

  useEffect(() => {
    if (copyMessage) {
      const timer = setTimeout(() => {
        setCopyMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copyMessage, setCopyMessage]);

  const { scrollToMessageWithRetry, handleScrollToMessage } =
    useScrollToMessage({
      messageRefs,
      messagesContainerRef,
      isProcessingUrlTargetRef: isProcessingUrlTarget,
      previousMessageThemeRef: previousMessageTheme,
      urlTargetMessageId,
      setWaitForAnimation,
      setIsSearchingMessage,
      updateMessageTheme,
      cleanMessageArray,
      fetchInitialMessages,
      messageNotFoundRef,
      messages,
    });

  const handleMessageNotFound = (fromUrl: boolean) => {
    showToast(
      "The message you are trying to find does not exist or is not accessible to you",
      "error",
    );

    if (fromUrl) {
      cleanupUrl();
      restoreOriginalDomainFocus();
    }

    setTargetMessageId(null);

    if (messages.length === 0) {
      setIsSearchingMessage(true);

      setTimeout(() => {
        cleanMessageArray().then(() => {
          fetchInitialMessages()
            .then(() => {
              setIsSearchingMessage(false);
            })
            .catch(() => {
              setIsSearchingMessage(false);
            });
        });
      }, 500);
    }
  };

  useTargetMessageEffect({
    targetMessageId,
    urlTargetMessageId,
    messageTheme,
    refetchTargetMessage,
    showLessonOnboarding,
    previousTargetMessageIdRef: previousTargetMessageId,
    skipRefetchRef,
    initialLoadCompletedRef: initialLoadCompleted,
    previousMessageThemeRef: previousMessageTheme,
    isProcessingUrlTargetRef: isProcessingUrlTarget,
    messagesContainerRef,
    animationTimeoutRef,
    setWaitForAnimation,
    setIsSearchingMessage,
    cleanMessageArray,
    fetchInitialMessages,
    updateMessageTheme,
    scrollToMessageWithRetry,
    messages,
    handleMessageNotFound,
  });

  const scrollToBottom = () => {
    if (targetMessageId) {
      setTargetMessageId(null);
      setMessages([]);
      void fetchInitialMessages()
        .then(() => {
          scrollToBottomJust(messagesContainerRef.current);
        })
        .catch((error) => {
          console.error("Failed to fetch initial messages while scrolling:", error);
        });
    } else {
      scrollToBottomSmooth(messagesContainerRef.current);
    }
  };

  const { isIOS } = useBrowserDetection();

  const scrollToBottomFast = () => {
    setTargetMessageId(null);
    void fetchInitialMessagesWithoutClearing()
      .then(() => {
        scrollToBottomJust(messagesContainerRef.current);

        if (isIOS) {
          setTimeout(() => {
            setShowScrollButton(false);
            setIsAtBottom(true);
          }, 100);
        }
      })
      .catch((error) => {
        console.error(
          "Failed to fetch additional messages while fast scrolling:",
          error,
        );
      });
  };

  const isScrollHandlerAttached = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Apply scroll blocking logic to all environments (localhost, staging, production)

    try {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } catch {}
    try {
      document.documentElement.style.scrollBehavior = "auto";
    } catch {}

    const el = messagesContainerRef.current;
    if (!el) return;

    (el.style as any).overflowAnchor = "none";
    el.style.scrollBehavior = "auto";

    const originalScrollTo = el.scrollTo.bind(el);

    let lastTop = el.scrollTop;
    let blockDownUntil = 0;

    const getTargetTop = (args: any[]): number | null => {
      if (typeof args[0] === "number") return args[0];
      if (args[0] && typeof args[0] === "object" && "top" in args[0]) {
        const t = Number((args[0] as any).top);
        return Number.isFinite(t) ? t : null;
      }
      return null;
    };

    const patchedScrollTo = (...args: any[]) => {
      const now = Date.now();
      if (now < blockDownUntil) {
        const targetTop = getTargetTop(args);
        if (targetTop !== null && targetTop > el.scrollTop) {
          return;
        }
        if (args[0] && typeof args[0] === "object") {
          args[0].behavior = "auto";
        }
      }
      return originalScrollTo(...(args as any));
    };

    const onScroll = () => {
      const goingUp = el.scrollTop < lastTop;
      lastTop = el.scrollTop;

      if (goingUp) {
        blockDownUntil = Date.now() + 1500;
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    (el as any).scrollTo = patchedScrollTo;

    return () => {
      try {
        el.removeEventListener("scroll", onScroll);
        (el as any).scrollTo = originalScrollTo;
      } catch {}
    };
  }, [messagesContainerRef]);

  const { scrollToBottomWhenAtBottom, scrollToBottomForMessageType } =
    useScrollHandling({
      messagesContainerRef,
      messageRefs,
      isScrollHandlerAttached,
      lastScrollTopRef,
      loadingOlder,
      targetMessageId,
      manualOverride,
      canPaginateUp,
      canPaginateDown,
      waitForAnimation,
      skipRefetchRef,
      setShowScrollButton,
      setIsAtBottom,
      setShowProgressBar,
      setTargetMessageId,
      loadOlderMessages,
      loadNewerMessages,
      isSmallScreen,
      scrollThresholdUp,
      scrollThresholdDown,
      MIN_SCROLL_TOP,
      isSearchingMessage,
      scrollTimeoutRef,
      messageInViewIdRef,
    });

  useEffect(() => {
    const handleOrientationChange = () => {
      const messageId = messageInViewIdRef.current;
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      const timeout = setTimeout(() => {
        setTargetMessageId(messageId);
      }, 300);
      resizeTimeoutRef.current = timeout;
    };

    window.addEventListener("resize", handleOrientationChange);

    return () => window.removeEventListener("resize", handleOrientationChange);
  }, [setTargetMessageId]);

  // Manage lessons to do fetching with debouncing and duplicate prevention
  useLessonsToDo({
    fetchLessonsToDo,
    focus,
    domain,
  });

  useLayoutEffect(() => {
    if (!bootstrapGeneration || !messages.length) return;
    if (handledBootstrapGenerationRef.current === bootstrapGeneration) return;

    handledBootstrapGenerationRef.current = bootstrapGeneration;
    setIsAtBottom(true);
    setShowScrollButton(false);

    bootstrapPinCancelRef.current?.();
    // Keep pinning briefly so late layout (quiz blocks, images) does not leave
    // the feed stuck above the newest messages after an offline reload.
    bootstrapPinCancelRef.current = pinScrollToBottom(
      messagesContainerRef.current,
    );
  }, [bootstrapGeneration, messages.length]);

  useEffect(() => {
    return () => {
      bootstrapPinCancelRef.current?.();
      bootstrapPinCancelRef.current = null;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!refreshChat) return;

      await cleanMessageArray();
      await bootstrapChat({ force: true });
      await setRefreshedLeaderBoard(true);
      await setRefreshedProgressBar(true);
      await setRefreshChat(false);
    };

    fetchData();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && refreshScheduled) {
        setRefreshScheduled(false);
        setRefreshChat(true);

        if (scheduledTargetMessageId) {
          setTargetMessageId(scheduledTargetMessageId);
          setScheduledTargetMessageId(null);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    refreshChat,
    refreshScheduled,
    scheduledTargetMessageId,
    cleanMessageArray,
    bootstrapChat,
    setRefreshedLeaderBoard,
    setRefreshedProgressBar,
    setRefreshChat,
    setRefreshScheduled,
    setScheduledTargetMessageId,
    setTargetMessageId,
  ]);

  useEffect(() => {
    scrollToBottomWhenAtBottom(isAtBottom);
  }, [isAtBottom, scrollToBottomWhenAtBottom]);

  // IntersectionObserver for reliable pagination
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const top = el.querySelector("#top-sentinel");
    const bottom = el.querySelector("#bottom-sentinel");
    if (!top || !bottom) return;

    const ioOpts: IntersectionObserverInit = {
      root: el,
      rootMargin: "0px",
      threshold: 0.01,
    };

    let topLock = false;
    const topIO = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (isBootstrapping) return;
      if (e.isIntersecting && !topLock && !loadingOlder && canPaginateUp) {
        topLock = true;
        loadOlderMessages().finally(() => {
          setTimeout(() => {
            topLock = false;
          }, 150);
        });
      }
    }, ioOpts);

    let bottomLock = false;
    const bottomIO = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (
        e.isIntersecting &&
        !bottomLock &&
        !skipRefetchRef.current &&
        (targetMessageId || canPaginateDown)
      ) {
        bottomLock = true;
        loadNewerMessages().finally(() => {
          setTimeout(() => {
            bottomLock = false;
          }, 150);
        });
      }
    }, ioOpts);

    topIO.observe(top);
    bottomIO.observe(bottom);

    return () => {
      topIO.disconnect();
      bottomIO.disconnect();
    };
  }, [
    messagesContainerRef,
    canPaginateUp,
    canPaginateDown,
    targetMessageId,
    loadNewerMessages,
    loadOlderMessages,
    skipRefetchRef,
    loadingOlder,
    isBootstrapping,
  ]);

  // Auto-scroll for specific message types
  const lastTailIdRef = useRef<string | null>(null);
  const pendingMenuAutoScrollUntilRef = useRef(0);
  const previousTargetMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    const handleMessageMenuActionStarted = () => {
      pendingMenuAutoScrollUntilRef.current = Date.now() + 6000;
    };

    window.addEventListener(
      "messageMenuActionStarted",
      handleMessageMenuActionStarted,
    );
    return () => {
      window.removeEventListener(
        "messageMenuActionStarted",
        handleMessageMenuActionStarted,
      );
    };
  }, [messagesContainerRef]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    const newTailId = last?.message_id ?? null;
    const tailChanged = newTailId && newTailId !== lastTailIdRef.current;
    if (tailChanged && last?.message_type) {
      const shouldForceMenuAutoScroll =
        Date.now() <= pendingMenuAutoScrollUntilRef.current;
      const isSystemMessage = last.message_type === "system";
      if (shouldForceMenuAutoScroll && messagesContainerRef.current) {
        window.setTimeout(() => {
          messagesContainerRef.current?.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }, 180);
      } else if (isSystemMessage && messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        const scrollBottom =
          container.scrollHeight -
          (container.scrollTop + container.clientHeight);
        const isNearBottom = scrollBottom < 500;

        if (isNearBottom || targetMessageId !== null) {
          setTimeout(() => {
            if (messagesContainerRef.current) {
              scrollToBottomJust(messagesContainerRef.current);
            }
          }, 300);
        }
      } else {
        scrollToBottomForMessageType(last.message_type, isAtBottom);
      }
      lastTailIdRef.current = newTailId;
    }
  }, [
    messages,
    isAtBottom,
    scrollToBottomForMessageType,
    messagesContainerRef,
    targetMessageId,
  ]);

  // Auto-scroll to bottom when targetMessageId is cleared after lesson messages are loaded
  useEffect(() => {
    const targetWasSet = previousTargetMessageIdRef.current !== null;
    const targetCleared = targetMessageId === null && targetWasSet;

    if (targetCleared && messages.length > 0 && messagesContainerRef.current) {
      const timeoutId = setTimeout(() => {
        if (messagesContainerRef.current) {
          scrollToBottomJust(messagesContainerRef.current);
        }
      }, 300);

      previousTargetMessageIdRef.current = targetMessageId;

      return () => clearTimeout(timeoutId);
    }

    previousTargetMessageIdRef.current = targetMessageId;
  }, [targetMessageId, messages.length, messagesContainerRef]);

  const { handleSendMessage } = useMessageSending({
    targetMessageId,
    setIsHeaderVisible,
    setManualOverride,
    scrollToBottom,
    sendMessage,
    user,
    sendMessageSocket,
    setMessages,
    messagesContainerRef,
    messageTheme,
    updateMessageTheme,
    themeChangeFromMessageRef,
    cleanMessageArray,
    fetchInitialMessages,
    scrollToBottomInitial,
  });

  const handleFeedbackClick = useCallback(() => {
    if (!useAppStore.getState().isOnline || isBrowserOffline()) {
      notifyOfflineActionBlocked();
      return;
    }
    window.dispatchEvent(new CustomEvent("feedbackSupportClicked"));
    handleSendMessage(feedbackCommand, false, true);
  }, [feedbackCommand, handleSendMessage]);

  const handleLogout = () => {
    void logout().then(() => {
      navigate("/login");
    });
  };

  const handleToggleHeader = () => {
    if (isHeaderVisible) {
      setIsHeaderVisible(false);
      setManualOverride(true);
    } else {
      setIsHeaderVisible(true);
      setShowProgressBar(true);
      setManualOverride(false);
      triggerAvatarRingsAnimation();
      setChevronAnimKey((k) => k + 1);
    }
  };

  const handleCollapseHeaderOnMessageSelect = useCallback(() => {
    setIsHeaderVisible(false);
    setManualOverride(true);
  }, []);

  const handleScrollPrev = async (currentMessageId: string) => {
    if (isBrowserLocked) return;

    if (messagesContainerRef.current) {
      try {
        await scrollToPreviousUserMessage(
          messagesContainerRef.current,
          messages,
          currentMessageId,
          fetchUserMessagePagination,
          cleanMessageArray,
        );
      } catch (error) {
        console.error("Failed to scroll to previous message:", error);
      }
    }
  };

  const handleScrollNext = async (currentMessageId: string) => {
    if (isBrowserLocked) return;

    if (messagesContainerRef.current) {
      try {
        await scrollToNextUserMessage(
          messagesContainerRef.current,
          messages,
          currentMessageId,
          fetchUserMessagePagination,
          cleanMessageArray,
        );
      } catch (error) {
        console.error("Failed to scroll to next message:", error);
      }
    }
  };

  const handleScrollPrevLesson = async (currentMessageId: string) => {
    if (isBrowserLocked) {
      return;
    }
    try {
      await fetchLessonPagination(currentMessageId, "up");
    } catch (error) {
      console.error("Failed to scroll to previous lesson message:", error);
    }
  };

  const handleScrollNextLesson = async (currentMessageId: string) => {
    if (isBrowserLocked) {
      return;
    }
    try {
      await fetchLessonPagination(currentMessageId, "down");
    } catch (error) {
      console.error("Failed to scroll to next lesson message:", error);
    }
  };

  const headerVisible = manualOverride ? isHeaderVisible : showProgressBar;
  const feedbackButtonBottom = 36;
  const feedbackButtonRight = isMobileScreen ? 6 : 14;
  const isFeedbackDisabled = messageTheme === "mmk" || sellingDialogue;

  if (
    showLessonOnboarding &&
    !isAssessmentOpen &&
    !isPreassessmentOpen
  ) {
    return (
      <Box sx={{ margin: "0 auto" }}>
        <LessonOnboarding />
      </Box>
    );
  }

  return (
    <Box
      className="w-full flex flex-col"
      sx={{
        background: theme.palette.chatComponent.mainBg,
        position: "relative",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <QuizePopUp open={isPreassessmentOpen} onClose={closeQuizModal} />
      <AssessmentPopUp open={isAssessmentOpen} onClose={closeAssessmentModal} />
      <AssessmentListModal />
      {isSearchingMessage && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9998,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {messageAudio && (
        <motion.div
          style={
            {
              position: "absolute",
              bottom: 112,
              left: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              zIndex: 5,
            } as React.CSSProperties
          }
          animate={{
            y: isOpenMiniPlayer ? 0 : 100,
            opacity: isOpenMiniPlayer ? 1 : 0,
          }}
          key={messageAudio}
          initial={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <MinimalAudioPlayer
            handleClose={handleToggleMiniPlayer}
            audio={messageAudio}
          />
        </motion.div>
      )}

      {isOnline && !headerVisible && (
        <button
          onClick={handleToggleHeader}
          style={{
            position: "absolute",
            top: 0,
            right: "50%",
            transform: "translateX(50%)",
            zIndex: 100,
            background: theme.palette.header.bgColor,
            border: "none",
            cursor: "pointer",
            borderBottomLeftRadius: "5px",
            borderBottomRightRadius: "5px",
            padding: "5px",
          }}
        >
          <MdLeaderboard
            size={24}
            style={{
              color: theme.palette.header.userBlock.nameColor,
              transition: "transform 0.3s",
            }}
          />
        </button>
      )}

      {/* Keep ChatHeader mounted while offline so open user-menu modals
          (team assessments, history, settings, etc.) are not unmounted. */}
      <motion.div
        style={
          {
            background: theme.palette.header.bgColor,
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 100,
            paddingTop: isLandscape ? "3px" : isSmallScreen ? "5px" : "10px",
            maxHeight: isLandscape && isSmallScreen ? "100vh" : undefined,
            overflowY: isLandscape && isSmallScreen ? "auto" : "visible",
            boxShadow:
              isPreassessmentOpen || isAssessmentOpen
                ? "0 0 10px rgba(0, 0, 0, 0.2)"
                : "none",
          } as React.CSSProperties
        }
        animate={{
          y: headerVisible
            ? 0
            : isLandscape
              ? -400
              : isSmallScreen
                ? -470
                : -480,
        }}
        transition={{ duration: 0.3 }}
      >
        <ChatHeader
          onLogout={handleLogout}
          onMessageSelect={handleScrollToMessage}
          onCollapseHeaderOnMessageSelect={handleCollapseHeaderOnMessageSelect}
        />
        <ProgressBarsBlocks />
        <LeaderBoard />
        {headerVisible && <PioneerLounge />}
        {headerVisible && (
          <div
            style={{
              textAlign: "center",
              marginTop: isLandscape ? "3px" : isSmallScreen ? "5px" : "10px",
              position: isLandscape && isSmallScreen ? "sticky" : "static",
              bottom: isLandscape && isSmallScreen ? 0 : undefined,
              background: theme.palette.header.bgColor,
              paddingBottom: isLandscape && isSmallScreen ? "6px" : undefined,
              zIndex: 1,
            }}
          >
            <button
              onClick={handleToggleHeader}
              style={{
                background: theme.palette.header.bgColor,
                border: "none",
                cursor: "pointer",
                padding: "5px",
              }}
            >
              <TripleChevron
                key={chevronAnimKey}
                size={24}
                color={theme.palette.header.userBlock.linkColor}
                isExpanded={headerVisible}
              />
            </button>
          </div>
        )}
      </motion.div>

      {messageTheme === "mmk" ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <MmkSection onCardInteract={handleCollapseHeaderOnMessageSelect} />
        </Box>
      ) : (
        <>
          <div
            className={`flex-1 ${isPreassessmentOpen || isAssessmentOpen ? "overflow-y-hidden" : "overflow-y-auto"} messages-container-chat messages-container`}
            ref={messagesContainerRef}
            style={{
              paddingBottom: 0,
              pointerEvents:
                isPreassessmentOpen || isAssessmentOpen ? "none" : "initial",
            }}
          >
            <div id="top-sentinel" style={{ height: 1 }} />
            <Box sx={{ height: 5 }} />
            <div className="max-w-6xl mx-auto">
              {uniqueMessages.map((msg, index) => (
                <div
                  key={msg.message_id}
                  id={`message-${msg.message_id}`}
                  ref={(el) => {
                    messageRefs.current[msg.message_id] = el;
                    if (!msg.is_bot && el) {
                      setLastUserMessageHeight(el.clientHeight + 165);
                    }
                  }}
                >
                  <MessageBubble
                    key={msg.message_id}
                    headerVisible={headerVisible}
                    scrollToPreviousUserMessage={handleScrollPrev}
                    scrollToNextUserMessage={handleScrollNext}
                    scrollToPreviousLessonMessage={handleScrollPrevLesson}
                    scrollToNextLessonMessage={handleScrollNextLesson}
                    message={msg}
                    isBot={msg.is_bot}
                    previousMessage={
                      index > 0 ? uniqueMessages[index - 1] : undefined
                    }
                    nextMessage={
                      index < uniqueMessages.length - 1
                        ? uniqueMessages[index + 1]
                        : undefined
                    }
                    messageEl={messageRefs.current[msg.message_id]}
                    setMessageRef={(el) => {
                      messageRefs.current[msg.message_id] = el;
                    }}
                    prevMessageHeight={lastUserMessageHeight}
                  />
                </div>
              ))}
            </div>
            <div id="bottom-sentinel" style={{ height: 1 }} />
          </div>
        </>
      )}

      <div
        style={{
          overflowX: isAnimating ? "hidden" : "visible",
          zIndex: 20,
          flexShrink: 0,
        }}
      >
        <MessageFooter
          onSendMessage={(msg, placeholder, hide, options) =>
            handleSendMessage(msg, placeholder, hide, options)
          }
          onFeedbackClick={handleFeedbackClick}
          onRecordAudio={openVoiceRecorder}
          onFileChange={(e, type) =>
            handleFileChange(
              e,
              type,
              uploadFile,
              handlePhotoUpload,
              handleFileUpload,
            )
          }
          isUploading={isUploading}
          isUploadingFile={isUploadingFile}
          setKeyboardState={setIsKeyboardOpen}
          feedbackDisabled={isFeedbackDisabled}
          isAudioRecorderOpen={showVoiceRecorder}
          onAudioRecorderClose={closeVoiceRecorder}
          sendMessageSocket={sendMessageSocket}
          currentUser={user}
          scrollToBottom={scrollToBottom}
        />
      </div>
      {!isAssessmentOpen &&
        !isPreassessmentOpen &&
        !isUserMenuModalOpen &&
        messageTheme !== "mmk" && (
        <FeedbackSupportButton
          onClick={handleFeedbackClick}
          tooltipTitle="Need help?"
          disabled={isFeedbackDisabled}
          bottom={isMobileScreen ? 4 : feedbackButtonBottom}
          right={isMobileScreen ? 0 : feedbackButtonRight}
        />
      )}

      <AnimatePresenceFixedType mode="wait">
        {showScrollButton && messageTheme !== "mmk" && (
          <motion.button
            onClick={() => {
              scrollToBottomFast();
            }}
            className={`bg-opacity-60 fixed w-[40px] h-[40px] right-4 bg-blue-500 z-50 text-white rounded-full shadow-lg hover:bg-blue-600 transition flex items-center justify-center ${isKeyboardOpen ? "bottom-[300px]" : "bottom-[125px]"}`}
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: -20, opacity: 0.7 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDownIcon
              size={24}
              style={{ transform: "translate(0, 2px)" }}
            />
          </motion.button>
        )}
      </AnimatePresenceFixedType>
      <AnimatePresence>
        {copyMessage && (
          <MotionBox
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.1, y: -20 }}
            transition={{ duration: 0.5 }}
            sx={{
              position: "fixed",
              bottom: 110,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "grey.600",
              color: "white",
              p: 0.8,
              m: 1,
              borderRadius: 1.3,
              zIndex: 1000,
            }}
          >
            {copyMessage}
          </MotionBox>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Chat;
